// src/features/admin/bulkOrderApi.ts
import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';

// Bulk Order Interfaces
export interface BulkOrderRequest {
  user_id: number;
  user_name?: string;
  user_email?: string;
  product_id: number;
  product_name?: string;
  product_quantity: number;
  min_qty: number;
  max_qty: number;
  status?: number; // 0=pending, 1=approved, 2=rejected
  created_at?: string;
  updated_at?: string;
}

export interface BulkOrder {
  id: number;
  request_id?: number;
  user_id: number;
  user_name?: string;
  user_email?: string;
  product_id: number;
  product_name?: string;
  prod_name?: string; // Alternative field name from API
  prod_short_name?: string; // Alternative field name from API
  product_main_image?: string;
  product_price?: number;
  currency?: string;
  product_quantity: number;
  min_qty: number;
  max_qty: number;
  status: number; // 0=pending, 1=approved, 2=rejected
  created_at: string;
  updated_at?: string;
}

export interface BulkOrderListParams {
  page?: number;
  limit?: number;
  search_str?: string;
  status?: number; // Filter by status
  user_id?: number; // Filter by user
  product_id?: number; // Filter by product
  sortby?: string;
  sortorder?: string;
}

export interface UpdateBulkOrderStatusRequest {
  request_id: number;
  status: number; // 1=approved, 2=rejected
}

export interface SetBulkOrderByProductRequest {
  product_id: number;
  bulkorder: 0 | 1; // 0=disable, 1=enable
}

export interface ApiResponse<T = any> {
  error: boolean;
  message: string;
  result?: T;
  data?: T;
  count?: number;
  total?: number;
  total_records?: number;
  success?: boolean;
  access?: boolean;
}

// API Configuration
export const BULK_ORDER_API_CONFIG = {
  baseUrl: process.env.REACT_APP_SERVER_ENDPOINT,
  AUTH_KEY: '7b4e1fd03f57885c1ff900ad76ea3810bb7160c7157a4c31cf2de3',

  ENDPOINTS: {
    // List all bulk order requests
    LIST_BULK_ORDERS: '/user/listallbulkorders',
    
    // Update bulk order status (approve/reject)
    UPDATE_BULK_ORDER_STATUS: '/user/updatebulkOrder',
    
    // Set product as bulk order eligible
    SET_PRODUCT_BULK_ORDER: '/user/setProductBulkOrder',
    
    // Get bulk order count
    BULK_ORDER_COUNT: '/user/bulkOrderCount',
  },
};

// Bulk Order API
export const bulkOrderApi = createApi({
  reducerPath: 'bulkOrderApi',
  baseQuery: fetchBaseQuery({
    baseUrl: BULK_ORDER_API_CONFIG.baseUrl,
    prepareHeaders: (headers) => {
      const token = localStorage.getItem('admin_token') || localStorage.getItem('vendor_token');
      const userRole = localStorage.getItem('user_role');

      if (token) {
        headers.set('Authorization', `Bearer ${token}`);
      }

      headers.set('authkey', BULK_ORDER_API_CONFIG.AUTH_KEY);
      headers.set('Content-Type', 'application/json');

      // Set vendor ID for vendor users
      if (userRole === 'vendor') {
        const vendorId = localStorage.getItem('vendor_id');
        if (vendorId) {
          headers.set('vendor-id', vendorId);
        }
      }

      return headers;
    },
  }),
  tagTypes: ['BulkOrder'],
  endpoints: (build) => ({
    // 1. List Bulk Orders
    listBulkOrders: build.query<ApiResponse<BulkOrder[]>, BulkOrderListParams>({
      query: ({ page = 1, limit = 10, search_str = '', status, user_id, product_id, sortby = 'created_at', sortorder = 'desc' } = {}) => ({
        url: BULK_ORDER_API_CONFIG.ENDPOINTS.LIST_BULK_ORDERS,
        method: 'POST',
        body: JSON.stringify({
          start: (page - 1) * limit,
          limit,
          search_str,
          ...(status !== undefined && { status }),
          ...(user_id && { user_id }),
          ...(product_id && { product_id }),
          sortby,
          sortorder,
        }),
      }),
      providesTags: ['BulkOrder'],
      transformResponse: (response: any) => {
        // Normalize the response data to ensure product_name is properly mapped
        const normalizedData = (response.result || response.data || []).map((order: any) => ({
          ...order,
          // Try to get product name from multiple possible field names
          product_name: order.product_name || order.prod_name || order.prod_short_name || null,
        }));

        return {
          error: response.error || false,
          message: response.message || '',
          result: normalizedData,
          count: response.count || response.total || response.total_records || 0,
        };
      },
    }),

    // 2. Get Bulk Order Count
    getBulkOrderCount: build.query<ApiResponse<{ count: number }>, { search_str?: string; status?: number; user_id?: number; product_id?: number }>({
      query: ({ search_str = '', status, user_id, product_id } = {}) => ({
        url: BULK_ORDER_API_CONFIG.ENDPOINTS.BULK_ORDER_COUNT,
        method: 'POST',
        body: JSON.stringify({
          search_str,
          ...(status !== undefined && { status }),
          ...(user_id && { user_id }),
          ...(product_id && { product_id }),
        }),
      }),
      providesTags: ['BulkOrder'],
    }),

    // 3. Update Bulk Order Status (Approve/Reject)
    updateBulkOrderStatus: build.mutation<ApiResponse<any>, UpdateBulkOrderStatusRequest>({
      query: ({ request_id, status }) => ({
        url: BULK_ORDER_API_CONFIG.ENDPOINTS.UPDATE_BULK_ORDER_STATUS,
        method: 'POST',
        body: JSON.stringify({
          request_id,
          status,
        }),
      }),
      invalidatesTags: ['BulkOrder'],
    }),

    // 4. Set Product Bulk Order Eligibility
    setProductBulkOrder: build.mutation<ApiResponse<any>, SetBulkOrderByProductRequest>({
      query: ({ product_id, bulkorder }) => ({
        url: BULK_ORDER_API_CONFIG.ENDPOINTS.SET_PRODUCT_BULK_ORDER,
        method: 'POST',
        body: JSON.stringify({
          product_id,
          bulkorder,
        }),
      }),
      invalidatesTags: ['BulkOrder'],
    }),

    // 5. Get Bulk Orders by Status
    getBulkOrdersByStatus: build.query<ApiResponse<BulkOrder[]>, { status: number; page?: number; limit?: number }>({
      query: ({ status, page = 1, limit = 10 }) => ({
        url: BULK_ORDER_API_CONFIG.ENDPOINTS.LIST_BULK_ORDERS,
        method: 'POST',
        body: JSON.stringify({
          start: (page - 1) * limit,
          limit,
          status,
        }),
      }),
      providesTags: ['BulkOrder'],
    }),

    // 6. Get Bulk Orders by Product
    getBulkOrdersByProduct: build.query<ApiResponse<BulkOrder[]>, { product_id: number; page?: number; limit?: number }>({
      query: ({ product_id, page = 1, limit = 10 }) => ({
        url: BULK_ORDER_API_CONFIG.ENDPOINTS.LIST_BULK_ORDERS,
        method: 'POST',
        body: JSON.stringify({
          start: (page - 1) * limit,
          limit,
          product_id,
        }),
      }),
      providesTags: ['BulkOrder'],
    }),

    // 7. Get Bulk Orders by User
    getBulkOrdersByUser: build.query<ApiResponse<BulkOrder[]>, { user_id: number; page?: number; limit?: number }>({
      query: ({ user_id, page = 1, limit = 10 }) => ({
        url: BULK_ORDER_API_CONFIG.ENDPOINTS.LIST_BULK_ORDERS,
        method: 'POST',
        body: JSON.stringify({
          start: (page - 1) * limit,
          limit,
          user_id,
        }),
      }),
      providesTags: ['BulkOrder'],
    }),
  }),
});

export const {
  useListBulkOrdersQuery,
  useGetBulkOrderCountQuery,
  useUpdateBulkOrderStatusMutation,
  useSetProductBulkOrderMutation,
  useGetBulkOrdersByStatusQuery,
  useGetBulkOrdersByProductQuery,
  useGetBulkOrdersByUserQuery,
} = bulkOrderApi;